import { CheckCircle2, Info, TriangleAlert, type LucideProps } from 'lucide-react'
import type { ComponentType, ReactNode } from 'react'

import { cn } from '@/lib/cn'

export type CalloutTone = 'info' | 'success' | 'warning' | 'danger'

interface ToneStyle {
  container: string
  icon: string
  Icon: ComponentType<LucideProps>
}

/* Tints are alpha derivations of the semantic tokens, so they still flip with
   the theme — no light/dark pair is hard-coded here. */
const TONES: Record<CalloutTone, ToneStyle> = {
  info: { container: 'border-border bg-surface', icon: 'text-text-muted', Icon: Info },
  success: {
    container: 'border-success/30 bg-success/10',
    icon: 'text-success',
    Icon: CheckCircle2,
  },
  warning: {
    container: 'border-warning/40 bg-warning/10',
    icon: 'text-warning',
    Icon: TriangleAlert,
  },
  danger: { container: 'border-danger/40 bg-danger/10', icon: 'text-danger', Icon: TriangleAlert },
}

export interface CalloutProps {
  tone?: CalloutTone
  /** Overrides the tone's default icon. */
  icon?: ComponentType<LucideProps>
  title?: string
  /**
   * Announce the contents when they appear. Only for something that happened in
   * response to the user — a failed save. Status that is simply true on load
   * should not interrupt a screen reader.
   */
  announce?: boolean
  children?: ReactNode
  className?: string
}

/**
 * A short block of consequence — a warning about where files land, a provider
 * that needs credentials. Body text stays at full contrast on purpose: this is
 * the thing the user has to read, not fine print.
 */
export function Callout({
  tone = 'info',
  icon,
  title,
  announce = false,
  children,
  className,
}: CalloutProps) {
  const style = TONES[tone]
  const Icon = icon ?? style.Icon

  return (
    <div
      className={cn('flex gap-2.5 rounded-lg border p-3', style.container, className)}
      role={announce ? 'alert' : undefined}
    >
      <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', style.icon)} aria-hidden="true" />
      <div className="min-w-0 flex-1 text-sm text-text">
        {title ? <p className="font-medium">{title}</p> : null}
        {children ? <div className={cn(title && 'mt-0.5')}>{children}</div> : null}
      </div>
    </div>
  )
}
